import { useMutation } from '@tanstack/react-query';
import { Button } from 'components/ui/button';
import * as Dialog from '@radix-ui/react-dialog';
import { Input } from 'components/ui/input';
import { LogoGenService } from 'features/text-to-logo/services/logo-gen.api';
import { LoaderIcon } from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { saveFile, setFileName } from 'store/slices/logoGen';

interface SaveImageModalProps {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export default function SaveImageModal({
  isOpen: externalIsOpen,
  onOpenChange,
}: SaveImageModalProps) {
  const [internalIsOpen, setInternalIsOpen] = useState(false);

  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const setIsOpen = onOpenChange || setInternalIsOpen;

  const {
    fileName,
    companyName,
    companyDescription,
    style,
    industry,
    tone,
    color,
    negativePrompt,
    tempLogoUrl,
  } = useSelector((state: RootState) => state.logoGen);

  const userId =
    useSelector((state: RootState) => state.auth.user?.userId) || '';
  const dispatch = useDispatch();

  const saveLogo = useMutation({
    mutationKey: ['save-generated-logo'],
    mutationFn: LogoGenService.saveGeneratedLogo,
    onSuccess: () => {
      setIsOpen(false);
      dispatch(saveFile({ fileSaved: true }));
      toast.success(`${fileName} saved `);
    },
  });

  const handleSubmit = (e: any) => {
    e.preventDefault();

    saveLogo.mutate({
      logo: {
        company_name: companyName,
        company_description: companyDescription,
        style,
        industry,
        tone,
        color,
        negative_prompt: negativePrompt,
      },
      user_id: userId,
      url: tempLogoUrl,
      name: fileName,
    });
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(e) => {
        if (!e) dispatch(setFileName({ fileName: 'Untitled 1' }));
        setIsOpen(e);
      }}
    >
      {!onOpenChange && (
        <Dialog.Trigger asChild>
          <Button
            variant="customPrimary"
            className="flex-1"
            size={'sm'}
          >
            Save
          </Button>
        </Dialog.Trigger>
      )}

      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-background-overlay/50 " />
        <Dialog.Content className="fixed text-text-primary top-1/2 left-1/2 w-full max-w-[340px] md:max-w-96 lg:max-w-[480px] -translate-x-1/2 -translate-y-1/2 bg-background-main p-6 rounded-lg shadow-lg">
          <Dialog.Title className="text-2xl font-bold text-primary">
            Save as
          </Dialog.Title>
          <form className="mt-4 text-text-primary">
            <div className="flex flex-col gap-2">
              <label htmlFor="fileName" className="block text-sm font-normal">
                Logo name
              </label>
              <Input
                name="fileName"
                value={fileName}
                onChange={(e) => {
                  const { value } = e.target;
                  dispatch(setFileName({ fileName: value }));
                }}
                type="text"
                min={2}
                className="w-full mt-[6px] px-3 py-2 border border-secondaryNeutral-7 placeholder-text-placeholder rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <Dialog.Close asChild>
                <Button
                  onClick={() => {
                    dispatch(setFileName({ fileName: 'Untitled 1' }));
                    setIsOpen(false);
                  }}
                  variant={'customSecondary'}
                  size={'sm'}
                  type="button"
                  className="px-3 w-auto h-8 text-sm font-medium rounded-[4px]"
                >
                  Cancel
                </Button>
              </Dialog.Close>
              <Button
                onClick={handleSubmit}
                variant={'customPrimary'}
                size={'sm'}
                disabled={saveLogo.isPending}
                className="px-3 w-auto h-8 rounded-[4px] text-white font-medium text-sm"
              >
                {saveLogo.isPending && (
                  <div className="animate-spin">
                    <LoaderIcon size={20} />
                  </div>
                )}
                {saveLogo.isPending ? 'Saving' : 'Save & Continue'}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
